import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Phone, X, Scissors, Bath, ShoppingBag, Camera } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import appApi from "./appApi";
import { Skeleton, stagger, rise, spring } from "./bits";

const CustomersScreen = () => {
  const { t, lang } = useLang();
  const m = t.mapp;
  const statuses = t.admin.statuses;
  const orderStatuses = t.account.order_status;
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [profile, setProfile] = useState(null);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (query.trim().length < 2) { setResults(null); return; }
    const tmr = setTimeout(async () => {
      try {
        const res = await appApi.get(`/app/customers/search?q=${encodeURIComponent(query.trim())}`);
        setResults(res.data);
      } catch { setResults([]); }
    }, 280);
    return () => clearTimeout(tmr);
  }, [query]);

  const open = async (phone) => {
    try {
      const res = await appApi.get(`/app/customers/${encodeURIComponent(phone)}/profile`);
      setProfile(res.data);
    } catch { toast.error(m.error_generic); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await appApi.post(`/app/customers/${encodeURIComponent(profile.phone)}/notes`, { text: note.trim() });
      setNote("");
      open(profile.phone);
    } catch { toast.error(m.error_generic); }
  };

  const setConsent = async (status) => {
    try {
      await appApi.post(`/app/customers/${encodeURIComponent(profile.phone)}/photo-consent`, { status });
      toast.success(m.consent_saved);
      open(profile.phone);
    } catch (err) { toast.error(err.response?.data?.detail || m.error_generic); }
  };

  const consentMeta = (pc) => {
    const st = pc?.status || "none";
    const styles = {
      granted: "bg-emerald-100 text-emerald-700 border-emerald-200",
      declined: "bg-rose-100 text-rose-600 border-rose-200",
      revoked: "bg-amber-100 text-amber-700 border-amber-200",
      none: "bg-stone-100 text-stone-500 border-stone-200",
    };
    return { label: m[`consent_status_${st}`], cls: styles[st] };
  };
  const fmtConsentAt = (iso) => (iso ? new Date(iso).toLocaleString(lang === "el" ? "el-GR" : "en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

  return (
    <div data-testid="app-customers-screen">
      <h1 className="font-heading text-3xl font-medium text-brand-ink">{m.cust_search_title}</h1>
      <div className="relative mt-4">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-brand/40" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={m.w_search_ph}
          className="w-full h-12 rounded-xl border border-brand/20 bg-white pl-12 pr-4 text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand/40"
          data-testid="app-customer-search"
        />
      </div>
      <motion.div variants={stagger} initial="hidden" animate="show" className="mt-4 space-y-2.5">
        {query.trim().length < 2 && <p className="text-sm text-brand-ink/40 italic py-8 text-center">{m.cust_empty}</p>}
        {results && results.length === 0 && <p className="text-sm text-brand-ink/40 italic py-8 text-center">{m.w_no_results}</p>}
        {results?.map((c) => (
          <motion.button
            key={c.phone}
            variants={rise}
            whileTap={{ scale: 0.98 }}
            onClick={() => open(c.phone)}
            className="w-full text-left rounded-2xl bg-white border border-brand/10 p-4 shadow-sm"
            data-testid={`app-customer-${c.phone}`}
          >
            <p className="font-bold text-brand-ink">{c.name || c.phone}</p>
            <p className="text-xs text-brand-ink/55 mt-0.5">
              {c.phone}{c.pets?.length ? ` · ${c.pets.join(", ")}` : ""} · {m.cust_bookings}: {c.bookings}
            </p>
          </motion.button>
        ))}
      </motion.div>

      <AnimatePresence>
        {profile && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" onClick={() => setProfile(null)} />
            <motion.div
              initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={spring}
              className="fixed inset-x-0 bottom-0 z-50 max-h-[88vh] overflow-y-auto rounded-t-[2rem] bg-brand-cream p-5 pb-10"
              data-testid="app-customer-profile"
            >
              <div className="mx-auto h-1.5 w-12 rounded-full bg-brand/20 mb-4" />
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="font-heading text-2xl font-medium text-brand-ink">{profile.name || profile.phone}</h2>
                  <a href={`tel:${profile.phone}`} className="text-sm text-brand font-bold no-a11y-underline">{profile.phone}</a>
                  {profile.email && <p className="text-xs text-brand-ink/55 mt-0.5">{profile.email}</p>}
                </div>
                <button onClick={() => setProfile(null)} className="grid place-items-center h-10 w-10 rounded-full bg-white border border-brand/15 text-brand-ink/60" data-testid="app-profile-close" aria-label="close">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="grid grid-cols-3 gap-2 mt-4">
                {[
                  [m.cust_bookings, profile.stats?.total_bookings ?? 0],
                  [m.cust_revenue, `€${profile.stats?.revenue ?? 0}`],
                  [m.cust_last, profile.stats?.last_visit || "—"],
                ].map(([label, val]) => (
                  <div key={label} className="rounded-xl bg-white border border-brand/10 p-3 text-center">
                    <p className="font-heading text-lg font-bold text-brand truncate">{val}</p>
                    <p className="text-[10px] font-bold uppercase text-brand-ink/40">{label}</p>
                  </div>
                ))}
              </div>

              {/* Photo/video consent status + management */}
              <div className="mt-5 rounded-2xl bg-white border border-brand/10 p-4" data-testid="app-consent-card">
                <div className="flex items-center gap-2">
                  <Camera className="h-4 w-4 text-brand shrink-0" />
                  <p className="text-xs font-bold uppercase tracking-widest text-brand-ink/40">{m.consent_label}</p>
                </div>
                {(() => { const cm = consentMeta(profile.photo_consent); return (
                  <div className="mt-2.5 flex items-center gap-2 flex-wrap">
                    <span className={`text-xs font-bold px-3 py-1.5 rounded-full border ${cm.cls}`} data-testid="app-consent-status">{cm.label}</span>
                    {profile.photo_consent?.at && (
                      <span className="text-[11px] text-brand-ink/45" data-testid="app-consent-at">{fmtConsentAt(profile.photo_consent.at)}</span>
                    )}
                  </div>
                ); })()}
                <div className="mt-3 grid grid-cols-3 gap-2">
                  <button onClick={() => setConsent("granted")} className="h-10 rounded-xl bg-emerald-500 text-white text-[11px] font-bold" data-testid="app-consent-grant">{m.consent_set_yes}</button>
                  <button onClick={() => setConsent("declined")} className="h-10 rounded-xl bg-rose-500 text-white text-[11px] font-bold" data-testid="app-consent-decline">{m.consent_set_no}</button>
                  <button onClick={() => setConsent("revoked")} className="h-10 rounded-xl bg-amber-500 text-white text-[11px] font-bold" data-testid="app-consent-revoke">{m.consent_set_revoke}</button>
                </div>
              </div>

              <p className="text-xs font-bold uppercase tracking-widest text-brand-ink/40 mt-5 mb-2">{m.cust_history}</p>
              <div className="space-y-2">
                {(profile.history || []).slice(0, 20).map((hst) => (
                  <div key={`${hst.type}-${hst.id}`} className="flex items-center gap-3 rounded-xl bg-white border border-brand/10 px-3 py-2.5 text-sm">
                    {hst.type === "grooming" ? <Scissors className="h-4 w-4 text-brand shrink-0" /> : hst.type === "selfwash" ? <Bath className="h-4 w-4 text-sky-600 shrink-0" /> : <ShoppingBag className="h-4 w-4 text-amber-600 shrink-0" />}
                    <div className="min-w-0">
                      <p className="font-bold text-brand-ink font-mono text-[13px]">{hst.date}{hst.time ? ` · ${hst.time}` : ""}</p>
                      <p className="text-xs text-brand-ink/55 truncate">{hst.detail}{hst.service ? ` · ${hst.service}` : ""}</p>
                    </div>
                    <span className="ml-auto text-xs text-brand-ink/50 shrink-0">
                      {hst.type === "order" ? (orderStatuses[hst.status] || hst.status) : (statuses[hst.status] || hst.status)}
                    </span>
                  </div>
                ))}
                {(profile.history || []).length === 0 && <p className="text-sm text-brand-ink/40 italic">—</p>}
              </div>

              <p className="text-xs font-bold uppercase tracking-widest text-brand-ink/40 mt-5 mb-2">{m.cust_notes}</p>
              <div className="space-y-2">
                {(profile.notes || []).map((n) => (
                  <div key={n.id} className="rounded-xl bg-amber-50 border border-amber-200/60 px-3 py-2.5 text-sm text-brand-ink">{n.text}</div>
                ))}
                <div className="flex gap-2">
                  <input value={note} onChange={(e) => setNote(e.target.value)} placeholder={m.cust_note_ph} className="flex-1 h-11 rounded-xl border border-brand/20 bg-white px-4 text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand/40" data-testid="app-note-input" />
                  <motion.button whileTap={{ scale: 0.94 }} onClick={addNote} className="h-11 px-5 rounded-xl bg-brand text-brand-cream font-bold" data-testid="app-note-add">{m.cust_note_add}</motion.button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CustomersScreen;
